/**
 * useRetryCancellationSync - Hook para reenviar ao provedor um cancelamento
 * que ficou em `falha_cancelamento`.
 *
 * Fluxo:
 *  1. Chama a RPC `retry_cancellation_sync` (cobrança OU parcela).
 *  2. A RPC volta o status para `processando_cancelamento` e reenfileira
 *     `cancel_charge` em q_charge. O worker e a Edge `financial-charge`
 *     finalizam para `cancelado` (sucesso) ou novamente `falha_cancelamento`
 *     (erro), espelhando o padrão `edicao_andamento → cancelado/falha_edicao`.
 *
 * RELEASE: 2026-06-19 v1.0.0 - Versão inicial.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useRetryCancellationSync');

export interface RetryCancellationPayload {
  cobranca_id?: string | null;
  parcela_id?: string | null;
}

export function useRetryCancellationSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: RetryCancellationPayload) => {
      logger.debug('Reenviando cancelamento ao provedor', payload);

      const { data, error } = await supabase.rpc('retry_cancellation_sync', {
        p_cobranca_id: payload.cobranca_id ?? null,
        p_parcela_id: payload.parcela_id ?? null,
      });

      if (error) {
        logger.error('Erro ao chamar retry_cancellation_sync', { error: error.message });
        throw error;
      }
      return data as { success: boolean; sync_enqueued: number };
    },
    onSuccess: (_data, variables) => {
      toast.success('Cancelamento reenviado ao provedor. Aguardando confirmação.');
      if (variables.cobranca_id) {
        queryClient.invalidateQueries({ queryKey: ['cobranca-details', variables.cobranca_id] });
      }
      queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas-paginated'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao reenviar cancelamento');
    },
  });
}
